import { z } from 'zod';

import type { CountryCode } from '@apple/utils/globalization/models';
import { countryCodeSchema } from '@apple/utils/globalization/models';

import {
	type CountryAddressRequirement,
	countryAddressRequirementSchema,
} from '@apple/features/address/models';
import type { PlantRequest } from '@apple/features/plants/models/models';
import { plantRequestSchema } from '@apple/features/plants/models/models';

export const countryKeys = {
	all: ['country'],
	country: (countryCode: CountryCode) => ['country', countryCode],
	supported: ['country', 'supported'],
} as const;

/** AppleBrandedPrograms.Web.Areas.Admin.ApiControllers.CountryAdminApiController.CountryModel*/
export interface Country {
	name: string;
	code: CountryCode;
	cultureCode: string;
	plant?: PlantRequest;
	currencyCode: string;
	addressRequirementType: CountryAddressRequirement | null;
	isEligibleForReturnOrders: boolean;
}

export interface CountryFilters extends Partial<Country> {}

export const AddressRequirementSchema = z.enum(['Local', 'LocalAndInternational']);

/** AppleBrandedPrograms.Web.Areas.Admin.ApiControllers.CountryAdminApiController.CountryModel*/
export const countrySchema = z.object({
	name: z.string(),
	code: countryCodeSchema,
	cultureCode: z.string(),
	plant: plantRequestSchema.optional(),
	currencyCode: z.string(),
	addressRequirementType: countryAddressRequirementSchema.nullable(),
	isEligibleForReturnOrders: z.boolean(),
}) satisfies z.ZodType<Country>;

export const countryFiltersSchema = countrySchema.partial() as z.ZodType<CountryFilters>;

export const countryRequestSchema = countrySchema
	.extend({
		code: countryCodeSchema.nullable(),
		country: countrySchema.shape.name,
		currency: countrySchema.shape.currencyCode,
		language: countrySchema.shape.cultureCode,
	})
	.omit({ name: true, currencyCode: true, cultureCode: true });

export type CountryRequest = z.infer<typeof countryRequestSchema>;
